import React, { useState, useEffect } from "react";
import MileageUploadProgress from "./MileageUploadProgress";

import { handlePhotoUpload } from "../../filestackRequestViaApi";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setSessionStorage } from "../../../../sessionAndLocalStorage";
import {
  selectAppraisal,
  hasRequiredImagesForCategory,
  getNextPhotoGroup,
  selectImageUploadData,
  getHasImagesFlaggedForDeletion
} from "../../../../store/selectors";
import { addImages, appraisalSlice } from "../../../../store/slices/Appraisal";
import { MilageImageUploadsSlice } from "../../../../store/slices/MilageImageUploads";
import { errorMessages } from "../../../../data/filterTypes";
import { completeReappraisal } from "../../../../api/requests";
import CustomException from "../../../../errors";
import { useDispatch as useAxiosDispatch } from "../../context/axiosContext";

export default function MileageUploadProgressContainer() {
  const dispatch = useDispatch();
  const id = "mileage";
  const {
    currentUploadHelper,
    currentImageIdentifiers,
    images,
    requiredPhotoData
  } = useSelector(selectImageUploadData);
  const requiresImages = useSelector(getHasImagesFlaggedForDeletion);
  const nextPhotoGroup = useSelector(getNextPhotoGroup);
  const navigate = useNavigate();
  const axiosDispatch = useAxiosDispatch();

  const hasAllImagesForCategory = useSelector(
    hasRequiredImagesForCategory(currentUploadHelper)
  );

  const [loading, setLoading] = useState(false);
  const {
    data: { appraisalSessionId }
  } = useSelector(selectAppraisal);

  useEffect(() => {
    const currentHelper = requiredPhotoData.find((data) => data.id === id);
    dispatch(
      MilageImageUploadsSlice.actions.setCurrentUploadHelper(currentHelper)
    );
  }, [id, dispatch, requiredPhotoData]);

  async function handleComplete() {
    if (requiresImages) return navigate("/photo-gallery");

    await completeReappraisal(appraisalSessionId).then(({ status, data }) => {
      if (status === 200) {
        dispatch(appraisalSlice.actions.setIsOnline(true));
        navigate("/branch-valuation-confirmation");
      } else {
        throw new CustomException(data.error);
      }
    });
  }

  function handleSubmit() {
    const imagesWithoutBlob = images.map(({ imageSrc, ...rest }) => rest);

    setSessionStorage("images", imagesWithoutBlob);

    const imagePayload = currentImageIdentifiers.map((imageIdentifier) =>
      currentUploadHelper.tag === "Vehicle introduction"
        ? {
            imageIdentifier,
            imageTags: [
              {
                tagDescription: currentUploadHelper.tag
              },
              {
                tagDescription: currentUploadHelper.id
              }
            ]
          }
        : {
            imageIdentifier,
            imageTags: [
              {
                tagDescription: currentUploadHelper.tag
              }
            ]
          }
    );

    // images have been uploaded to filestack but not yet to appraisal
    if (currentImageIdentifiers.length > 0) {
      setLoading(true);
      const imageRequest = () =>
        dispatch(addImages({ images: imagePayload, appraisalSessionId })).then(
          (response) => {
            if (response.error?.message === errorMessages.NETWORKERROR) {
              dispatch(appraisalSlice.actions.setIsOnline(false));
              setTimeout(() => {
                imageRequest();
              }, 3000);
            } else {
              dispatch(appraisalSlice.actions.setIsOnline(true));
              dispatch(
                MilageImageUploadsSlice.actions.clearCurrentImageIdentifiers()
              );

              if (hasAllImagesForCategory) {
                // navigate to redirect url
                return handleComplete();
              }
            }
          }
        );
      imageRequest();
    }
  }

  const handleChange = async (images) => {
    if (images) {
      handlePhotoUpload(
        images,
        dispatch,
        currentUploadHelper,
        axiosDispatch,
        appraisalSessionId
      );
    }
    dispatch(MilageImageUploadsSlice.actions.setRetryingRequest(0));
  };

  return (
    <>
      {currentUploadHelper && (
        <MileageUploadProgress
          handleSubmit={handleSubmit}
          handleChange={handleChange}
          minPhotos={currentUploadHelper.imageQuantityRequired}
          currentUploadGroup={id}
          loading={loading}
          lastImage={!nextPhotoGroup}
          hasAllImages={hasAllImagesForCategory}
        />
      )}
    </>
  );
}
